"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Calendar03Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Counter } from "@/components/shared/Counter";
import { Typography } from "@/components/ui/typography";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import DateRangePickerModal from "../shared/DateRangePickerModal";
import formatPerisanDate from "@/utils/formatPerisanDate";
import { usePdpStayDates } from "@/hooks/usePdpStayDates";
import { usePdpPriceCalendar } from "@/lib/react-query/queries/accommodation/use-pdp-price-calendar";

const bookingFormSchema = z.object({
  dateRange: z
    .object({
      from: z.date(),
      to: z.date(),
    })
    .optional(),
  guests: z
    .number()
    .min(1, "Guest count must be at least 1")
    .default(1)
    .optional(),
});

type BookingFormValues = z.infer<typeof bookingFormSchema>;

interface BookingPopoverProps {
  accommodationId: string;
  onBook?: (data: BookingFormValues) => void;
}

export function BookingPopover({ accommodationId, onBook }: BookingPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [openDateRange, setOpenDateRange] = React.useState(false);
  const { checkIn, checkOut, capacity, dateRange, setStay } =
    usePdpStayDates();

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      guests: capacity,
      dateRange: {
        from: dateRange.from,
        to: dateRange.to,
      },
    },
  });

  // Keep the popover in sync when the URL stay window changes elsewhere
  React.useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;
    form.setValue("dateRange", {
      from: dateRange.from,
      to: dateRange.to,
    });
    form.setValue("guests", capacity);
  }, [checkIn, checkOut, capacity, form]);

  const selectedDateRange = form.watch("dateRange");
  const selectedGuests = form.watch("guests");
  const calendarCapacity =
    Number.isInteger(selectedGuests) && (selectedGuests as number) >= 1
      ? (selectedGuests as number)
      : capacity;

  const { availableDates, isAvailabilityPending } = usePdpPriceCalendar({
    accommodationId,
    capacity: calendarCapacity,
  });

  const onSubmit = (data: BookingFormValues) => {
    // One navigation for both — two pushes would fire an extra prices request
    setStay(data.dateRange, data.guests);
    onBook?.(data);
    setOpen(false);
  };

  let buttonLabel = "انتخاب تاریخ و نفرات";
  if (selectedDateRange?.from && selectedDateRange?.to) {
    const formattedFrom = formatPerisanDate(selectedDateRange.from);
    const formattedTo = formatPerisanDate(selectedDateRange.to);
    buttonLabel = `${formattedFrom} - ${formattedTo}، ${selectedGuests} نفر`;
  } else if (selectedGuests && selectedGuests > 1) {
    buttonLabel = `${selectedGuests} نفر`;
  } else if (selectedGuests === 1) {
    buttonLabel = `${selectedGuests} نفر`;
  }

  return (
    <>
      <div className="hidden">
        <Controller
          name="dateRange"
          control={form.control}
          render={({ field }) => (
            <DateRangePickerModal
              dateRange={
                field.value?.from && field.value?.to ? field.value : undefined
              }
              onSelect={field.onChange}
              open={openDateRange}
              setOpen={(isOp) => {
                if (isOp) setOpenDateRange(isOp);
                else {
                  setOpenDateRange(isOp);
                  setOpen(true);
                }
              }}
              availableDates={availableDates}
              isAvailabilityPending={isAvailabilityPending}
              constrainAvailability
            />
          )}
        />
      </div>
      <ResponsivePopover
        open={open}
        onOpenChange={setOpen}
        className="w-auto md:p-0"
        direction="bottom"
        sideOffset={16}
        trigger={
          <Button
            variant="outline"
            className="flex-1 md:hidden font-normal h-auto py-3 rounded-lg gap-2 text-sm"
          >
            <HugeiconsIcon icon={Calendar03Icon} className="size-5" />
            {buttonLabel}
          </Button>
        }
      >
        <form onSubmit={form.handleSubmit(onSubmit)} className="pt-6 space-y-6">
          <div className="flex items-center justify-between">
            <Typography variant="bodySRegular">ویرایش تاریخ</Typography>
            <Button
              type="button"
              variant={"secondary"}
              onClick={() => setOpenDateRange(!openDateRange)}
              className="bg-gray-50 text-foundation-textColor h-11"
            >
              ویرایش
            </Button>
          </div>

          <Controller
            name="guests"
            control={form.control}
            render={({ field }) => (
              <Counter
                label="تعداد نفرات"
                value={field.value || 1}
                onChange={field.onChange}
                min={1}
                className="p-0"
              />
            )}
          />

          <div className="flex justify-between gap-10 items-center md:mt-0 md:border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="p-1 md:p-1"
            >
              <HugeiconsIcon
                className="size-[18px] hidden md:block"
                icon={Cancel01Icon}
              />
              <Typography variant="bodySMedium">انصراف</Typography>
            </Button>
            <Button
              type="submit"
              className="flex-1 md:flex-none h-[50px] md:h-7 rounded-lg text-sm font-medium"
            >
              اعمال تغییرات
            </Button>
          </div>
        </form>
      </ResponsivePopover>
    </>
  );
}
